import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { Skeleton, Badge, Avatar } from "@/components/ds";
import { DashboardHeadline } from "@/components/app/dashboard/DashboardHeadline";
import { DashboardSummaryFooter } from "@/components/app/dashboard/DashboardSummary";
import { DashboardActivation } from "@/components/app/dashboard/DashboardActivation";
import { DashboardPreview } from "@/components/app/dashboard/DashboardPreview";
import { DashboardDigest } from "@/components/app/dashboard/DashboardDigest";
import type {
  DashboardShift,
  DashboardSummary,
  DashboardStatusGroup,
  DashboardShiftStatus,
  DashboardSetup,
  DashboardNextShift,
  DashboardWeekStats,
  DashboardYesterdayStats,
} from "@/domain/dashboard/types";
import { statusToGroup } from "@/domain/dashboard/status";
import { useAuth } from "../hooks/useAuth";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./DashboardPage.module.css";

const POLL_INTERVAL_MS = 30_000;

interface DashboardData {
  readonly shifts: DashboardShift[];
  readonly summary: DashboardSummary;
  readonly setup: DashboardSetup;
  readonly nextShift: DashboardNextShift | null;
  readonly weekStats: DashboardWeekStats | null;
  readonly yesterdayStats: DashboardYesterdayStats | null;
  readonly date: string;
}

function isSetupIncomplete(setup: DashboardSetup): boolean {
  return !setup.hasSite || !setup.hasCleaner || !setup.hasShift;
}

function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

function formatTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const period = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${period}` : `${h12}:${mStr}${period}`;
}

function formatCheckTime(isoTime: string): string {
  const d = new Date(isoTime);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function shiftDurationMinutes(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

function LiveProgress({
  checkInTime,
  startTime,
  endTime,
  verification,
}: {
  readonly checkInTime: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly verification: string | null;
}) {
  const [minutes, setMinutes] = useState(() =>
    Math.round((Date.now() - new Date(checkInTime).getTime()) / 60_000),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setMinutes(Math.round((Date.now() - new Date(checkInTime).getTime()) / 60_000));
    }, 60_000);
    return () => clearInterval(interval);
  }, [checkInTime]);

  const total = shiftDurationMinutes(startTime, endTime);
  const pct = total > 0 ? Math.min(100, Math.round((minutes / total) * 100)) : 0;

  return (
    <div>
      <div className={styles.progressRow}>
        <span className={styles.progressTime}>{formatCheckTime(checkInTime)}</span>
        <div className={styles.progressBarWrap}>
          <div className={styles.progressBar}>
            <div className={styles.progressFillLive} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <span className={styles.liveDuration} style={{ gridColumn: "span 2" }}>On site {formatDuration(minutes)}</span>
      </div>
      {verification && verification !== "verified" && (
        <div className={styles.progressMeta}>
          <span className={styles.gpsBadge}>
            {gpsBadgeLabels[verification] ?? verification}
          </span>
        </div>
      )}
    </div>
  );
}

// --- Status config ---

const statusBadge: Record<DashboardShiftStatus, { label: string; variant: "danger" | "warning" | "success" | "neutral" } | null> = {
  no_show: { label: "No show", variant: "danger" },
  late: { label: "Late", variant: "warning" },
  due_soon: { label: "Soon", variant: "neutral" },
  on_site: { label: "On site", variant: "success" },
  completed: { label: "Done", variant: "success" },
  short: { label: "Short", variant: "warning" },
  upcoming: null,
};

const statusStrip: Record<DashboardShiftStatus, string> = {
  no_show: styles.stripDanger,
  late: styles.stripWarning,
  due_soon: styles.stripNeutral,
  on_site: styles.stripSuccess,
  completed: styles.stripSuccess,
  short: styles.stripWarning,
  upcoming: "",
};

const sectionOrder: DashboardStatusGroup[] = ["needs_attention", "on_site", "upcoming", "completed"];

const sectionLabels: Record<DashboardStatusGroup, string> = {
  needs_attention: "Needs attention",
  on_site: "On site",
  upcoming: "Upcoming",
  completed: "Completed",
};

const gpsBadgeLabels: Record<string, string> = {
  manual_override: "Manual override",
  gps_unavailable: "GPS unavailable",
  gps_timeout: "GPS timeout",
  auto_closed: "Auto closed",
};

const COL_COUNT = 6;

function groupShifts(shifts: readonly DashboardShift[]): Map<DashboardStatusGroup, DashboardShift[]> {
  const groups = new Map<DashboardStatusGroup, DashboardShift[]>();
  for (const shift of shifts) {
    const group = statusToGroup(shift.status);
    const list = groups.get(group) ?? [];
    list.push(shift);
    groups.set(group, list);
  }
  return groups;
}

function ProgressCell({ shift }: { readonly shift: DashboardShift }) {
  const isUpcoming = shift.status === "upcoming" || shift.status === "due_soon";

  if (!shift.checkInTime) {
    if (isUpcoming) return <td className={styles.progressCell} />;
    return (
      <td className={styles.progressCell}>
        <span className={styles.notCheckedIn}>Not checked in</span>
      </td>
    );
  }

  const hasCheckOut = shift.checkOutTime !== null;

  if (hasCheckOut) {
    const total = shiftDurationMinutes(shift.startTime, shift.endTime);
    const actual = shift.durationMinutes ?? 0;
    const pct = total > 0 ? Math.min(100, Math.round((actual / total) * 100)) : 100;

    return (
      <td className={styles.progressCell}>
        <div className={styles.progressRow}>
          <span className={styles.progressTime}>{formatCheckTime(shift.checkInTime)}</span>
          <div className={styles.progressBarWrap}>
            <div className={styles.progressBar}>
              <div className={shift.status === "short" ? styles.progressFillShort : styles.progressFillDone} style={{ width: `${pct}%` }} />
            </div>
          </div>
          <span className={styles.progressTime}>{formatCheckTime(shift.checkOutTime!)}</span>
          {shift.durationMinutes !== null && (
            <span className={styles.progressDuration}>{formatDuration(shift.durationMinutes)}</span>
          )}
        </div>
      </td>
    );
  }

  return (
    <td className={styles.progressCell}>
      <LiveProgress
        checkInTime={shift.checkInTime}
        startTime={shift.startTime}
        endTime={shift.endTime}
        verification={shift.checkInVerification}
      />
    </td>
  );
}

function CallCell({ shift }: { readonly shift: DashboardShift }) {
  if (!shift.cleanerPhone) return null;
  return (
    <a
      href={`tel:${shift.cleanerPhone}`}
      className={styles.callLink}
      aria-label={`Call ${shift.cleanerName.split(" ")[0]}`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M3.654 1.328a.678.678 0 00-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 004.168 6.608 17.569 17.569 0 006.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 00-.063-1.015l-2.307-1.794a.678.678 0 00-.58-.122l-2.19.547a1.745 1.745 0 01-1.657-.459L5.482 8.062a1.745 1.745 0 01-.46-1.657l.548-2.19a.678.678 0 00-.122-.58L3.654 1.328z" />
      </svg>
    </a>
  );
}

function formatMinutesUntil(startTime: string): string {
  const [h, m] = startTime.split(":").map(Number);
  const now = new Date();
  const start = new Date(now);
  start.setHours(h, m, 0, 0);
  const diff = Math.round((start.getTime() - now.getTime()) / 60_000);
  if (diff <= 0) return "now";
  if (diff < 60) return `in ${diff}m`;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  if (mins === 0) return `in ${hours}h`;
  return `in ${hours}h ${mins}m`;
}

function StatusCell({ shift }: { readonly shift: DashboardShift }) {
  const badge = statusBadge[shift.status];
  const strip = statusStrip[shift.status];

  return (
    <td className={styles.statusCell}>
      {strip && <div className={`${styles.statusStrip} ${strip}`} />}
      <div className={styles.statusContent}>
        {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
        {shift.status === "upcoming" && (
          <span className={styles.startsIn}>{formatMinutesUntil(shift.startTime)}</span>
        )}
      </div>
    </td>
  );
}

function ShiftTableRow({ shift }: { readonly shift: DashboardShift }) {
  return (
    <tr className={styles.shiftRow}>
      <StatusCell shift={shift} />
      <td><span className={styles.siteName}>{shift.siteName}</span></td>
      <td className={styles.hideMobile}>
        <span className={styles.cleanerCell}>
          <Avatar name={shift.cleanerName} size="sm" />
          <span className={styles.cleanerName}>{shift.cleanerName}</span>
        </span>
      </td>
      <td><span className={styles.shiftTime}>{formatTimeRange(shift.startTime, shift.endTime)}</span></td>
      <ProgressCell shift={shift} />
      <td><CallCell shift={shift} /></td>
    </tr>
  );
}

function SectionHeaderRow({
  group,
  count,
  collapsed,
  onToggle,
}: {
  readonly group: DashboardStatusGroup;
  readonly count: number;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}) {
  const isNeedsAttention = group === "needs_attention";
  const collapsible = !isNeedsAttention;

  return (
    <tr
      className={`${styles.sectionRow} ${collapsible ? styles.sectionRowCollapsible : ""}`}
      onClick={collapsible ? onToggle : undefined}
    >
      <td colSpan={COL_COUNT}>
        <span className={styles.sectionContent}>
          {sectionLabels[group]} ({count})
          {collapsible && (
            <svg
              className={`${styles.sectionChevron} ${collapsed ? styles.sectionChevronCollapsed : ""}`}
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.22 6.22a.75.75 0 011.06 0L8 8.94l2.72-2.72a.75.75 0 111.06 1.06l-3.25 3.25a.75.75 0 01-1.06 0L4.22 7.28a.75.75 0 010-1.06z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
      </td>
    </tr>
  );
}

function SkeletonRows() {
  return (
    <div className={styles.skeletonList} aria-busy="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className={styles.skeletonRow}>
          <Skeleton width="60px" height="20px" />
          <Skeleton width="100px" height="14px" />
          <Skeleton width="80px" height="14px" />
          <Skeleton width="90px" height="14px" />
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  usePageTitle("Dashboard | TidyRota");
  const { user } = useAuth();
  const ownerFirstName = user.name.trim().split(/\s+/)[0] ?? user.name;

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set(["completed"]));
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch("/api/dashboard");
      if (!res.ok) return;
      const body = await res.json();
      if (body.success) {
        setData(body.data);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  // Collapse "upcoming" section when 10+ shifts arrive
  useEffect(() => {
    if (!data) return;
    const grouped = groupShifts(data.shifts);
    const upcoming = grouped.get("upcoming");
    if (upcoming && upcoming.length >= 10) {
      setCollapsedSections((prev) => {
        if (prev.has("upcoming")) return prev;
        return new Set([...prev, "upcoming"]);
      });
    }
  }, [data]);

  const toggleSection = useCallback((group: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <SkeletonRows />
      </div>
    );
  }

  if (!data) return null;

  const grouped = groupShifts(data.shifts);
  const showActivation =
    data.shifts.length === 0 && isSetupIncomplete(data.setup);
  const showDigest =
    data.shifts.length === 0 &&
    !showActivation &&
    data.setup.hasShift &&
    (data.nextShift !== null ||
      data.weekStats !== null ||
      data.yesterdayStats !== null);

  return (
    <div className={styles.page}>
      <DashboardHeadline summary={data.summary} hasShift={data.setup.hasShift} />

      {showActivation && (
        <div className={styles.activationStack}>
          <DashboardActivation setup={data.setup} ownerFirstName={ownerFirstName} />
          <DashboardPreview />
        </div>
      )}

      {showDigest && (
        <div className={styles.digestWrap}>
          <DashboardDigest
            today={data.date}
            nextShift={data.nextShift}
            weekStats={data.weekStats}
            yesterdayStats={data.yesterdayStats}
          />
        </div>
      )}

      {data.shifts.length > 0 && (
        <div className={styles.tableCard}>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <colgroup>
                <col style={{ width: "10%" }} />
                <col style={{ width: "30%" }} />
                <col className={styles.hideMobile} style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <tbody>
                {sectionOrder.map((group) => {
                  const sectionShifts = grouped.get(group);
                  if (!sectionShifts || sectionShifts.length === 0) return null;

                  const collapsed = collapsedSections.has(group);

                  return (
                    <Fragment key={group}>
                      <SectionHeaderRow
                        group={group}
                        count={sectionShifts.length}
                        collapsed={collapsed}
                        onToggle={() => toggleSection(group)}
                      />
                      {!collapsed &&
                        sectionShifts.map((shift) => (
                          <ShiftTableRow key={shift.shiftId} shift={shift} />
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DashboardSummaryFooter summary={data.summary} />
    </div>
  );
}
